import AreasTable from "./AreasTable";
import BaseClass from "../../util/BaseClass";
import Context from "../../context/Context";
import FieldNames from "../../aiim/datasets/FieldNames";
import Source from "../../aiim/base/Source";
import * as aiimUtil from "../../aiim/util/aiimUtil";
import * as portalUtil from "../../util/portalUtil";
import * as serviceUtil from "./serviceUtil";
import * as sourceUtil from "./sourceUtil";

export default class PlanOpener extends BaseClass {

  openHostedPlan(project,plan,planServiceItemId) {
    const promise = new Promise((resolve,reject) => {
      const i18n = Context.instance.i18n;
      plan.issues = [];
      const task = {
        plan: {
          serviceItemId: planServiceItemId
        }
      };
      portalUtil.readItem(task.plan.serviceItemId).then(result => {
        const item = result && result.data;
        task.plan.serviceItem = item;
        task.plan.serviceUrl = Context.checkMixedContent(item && item.url);
      }).then(result => {
        return portalUtil.readItemJsonData(task.plan.serviceItemId).then(result => {
          task.plan.serviceItemData = result && result.data;
        });
      }).then(result => {
        return serviceUtil.readServiceJson(task.plan.serviceUrl,task.plan);
      }).then(result => {
        const cap = task.plan.serviceInfo.capabilities.split(",");
        if (cap.indexOf("Editing") === -1) {
          plan.issues.push({
            key: "editingNotEnabled",
            message: i18n.spaceplanner.issues.editingNotEnabled
          });
        }
        if (!task.plan.peopleLayerInfo || !task.plan.unitsLayerInfo ||
            !task.plan.areasTableInfo) {
          plan.issues.push({
            key: "requiresDataset",
            message: i18n.spaceplanner.issues.requiresDataset
          });
        }
      }).then(result => {
        if (!plan.hasIssues()) return this._loadLayers(task);
      }).then(result => {
        if (!plan.hasIssues()) return this._makeSources(task);
      }).then(result => {
        plan.title = task.plan.serviceItem.title;
        plan.planPeopleSource = task.plan.peopleSource;
        plan.planUnitsSource = task.plan.unitsSource;
        plan.planServiceItem = task.plan.serviceItem;
        plan.areasTable = task.plan.areasTable;
      }).then(result => {
        if (!plan.hasIssues()) return this._addLayersToMap(task);
      }).then(result => {
        resolve();
      }).catch(ex => {
        reject(ex);
      });
    });
    return promise;
  }

  openVersionedPlan(project,plan,planId) {
    const promise = new Promise((resolve,reject) => {
      const versionedInfo = project.versionedInfo;
      const task = {
        plan: {
          serviceUrl: versionedInfo.sourceInfo.serviceUrl,
          serviceItem: versionedInfo.sourceInfo.serviceItem,
          peopleLayer: versionedInfo.peopleLayer,
          unitsLayer: versionedInfo.unitsLayer,
          areasTableInfo: versionedInfo.sourceInfo.areasTableInfo
        }
      };
      const versionManager = project.versionedInfo.versionManager;
      const versionInfo = versionManager.findVersionInfo(planId);
      const gdbVersion = versionInfo && versionInfo.versionName;
      Promise.resolve().then(() => {
        if (!versionInfo) {
          let err = new Error("The plan id is invalid");
          err.code = "__invalidPlanId__";
          throw err;
        }
      }).then(() => {
        return this._loadAreasTable(task,gdbVersion);
      }).then(() => {
        this._applyVersionToLayer(task.plan.peopleLayer,gdbVersion);
        this._applyVersionToLayer(task.plan.unitsLayer,gdbVersion);
      }).then(() => {
        return sourceUtil.makePlanSources(task);
      }).then(() => {
        plan.isVersioned = true;
        plan.versionInfo = versionInfo;
        plan.project = project;
        plan.title = versionManager.getVersionTitle(versionInfo);
        plan.planPeopleSource = task.plan.peopleSource;
        plan.planUnitsSource = task.plan.unitsSource;
        plan.planServiceItem = task.plan.serviceItem;
        plan.areasTable = task.plan.areasTable;
      }).then(() => {
        //console.log("openVersionedPlan",task)
        resolve();
      }).catch(ex => {
        reject(ex);
      });
    });
    return promise;
  }

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -  */

  _addLayersToMap(task) {
    const i18n = Context.instance.i18n;
    const view = Context.instance.views.mapView;
    const peopleLayer = task.plan.peopleLayer;
    const unitsLayer = task.plan.unitsLayer;

    if (view && unitsLayer) {
      let layers = view.map.layers;
      let index = null;
      layers.some((layer,idx) => {
        if (layer.xtnAiim && layer.xtnAiim.source &&
            layer.xtnAiim.source.isAiimUnits()) {
          index = idx;
          layer.visible = false;
          layer.listMode = "hide";
          return true;
        }
        return false;
      });
      unitsLayer.title = i18n.spaceplanner.units.planLayerTitle;
      view.map.add(unitsLayer,index);
    }

    if (view && peopleLayer) {
      let layers = view.map.layers;
      let index = null;
      layers.some((layer,idx) => {
        if (layer.xtnAiim && layer.xtnAiim.source &&
            layer.xtnAiim.source.isAiimPeople()) {
          index = idx;
          layer.visible = false;
          layer.listMode = "hide";
          return true;
        }
        return false;
      });
      peopleLayer.title = i18n.spaceplanner.people.planLayerTitle;;
      view.map.add(peopleLayer,index);
    }
  }

  _applyVersionToLayer(layer,gdbVersion) {
    if (layer && gdbVersion) {
      layer.gdbVersion = gdbVersion;
      layer.refresh();
    }
  }

  _loadLayers(task) {
    const p1 = this._loadPeopleLayer(task);
    const p2 = this._loadUnitsLayer(task);
    const p3 = this._loadAreasTable(task);
    return Promise.all([p1,p2,p3]);
  }

  _loadAreasTable(task,gdbVersion) {
    const lib = Context.instance.lib;
    const tableInfo = task.plan.areasTableInfo;
    task.plan.areasTable = new AreasTable();
    if (tableInfo) {
      let options;
      if (gdbVersion) {
        let url = task.plan.serviceUrl + "/" + tableInfo.id;
        options = {
          url: url,
          gdbVersion: gdbVersion
        }
      } else {
        options = {
          portalItem: {
            id: task.plan.serviceItemId
          },
          layerId: tableInfo.id
        }
      }
      const table = task.plan.areasTable.table = new lib.esri.FeatureLayer(options);
      return task.plan.areasTable.load(table);
    } else {
      return Promise.resolve();
    }
  }

  _loadPeopleLayer(task) {
    const lib = Context.instance.lib;
    const layerInfo = task.plan.peopleLayerInfo;
    if (layerInfo) {
      let layer = task.plan.peopleLayer = new lib.esri.FeatureLayer({
        portalItem: {
          id: task.plan.serviceItemId
        },
        layerId: layerInfo.id
      });
      layer.when().then(() => {
      });
      return layer.load();
    } else {
      return Promise.resolve();
    }
  }

  _loadUnitsLayer(task) {
    const lib = Context.instance.lib;
    const layerInfo = task.plan.unitsLayerInfo;
    if (layerInfo) {
      let layer = task.plan.unitsLayer = new lib.esri.FeatureLayer({
        portalItem: {
          id: task.plan.serviceItemId
        },
        layerId: layerInfo.id
      });
      layer.when().then(() => {
      });
      return layer.load();
    } else {
      return Promise.resolve();
    }
  }

  _makeSources(task) {
    const promises = [];
    const peopleLayer = task.plan.peopleLayer;
    const unitsLayer = task.plan.unitsLayer;
    if (peopleLayer) {
      let layer = peopleLayer;
      task.plan.peopleSource = new Source({
        key: sourceUtil.getPeopleSourceKey(),
        name: layer.title,
        url: layer.url+"/"+layer.layerId,
        layer2D: layer
      });
      let v = FieldNames.PEOPLE_FULLNAME;
      if (layer && aiimUtil.findField(layer.fields,v)) {
        task.plan.peopleSource.displayField = v;
      }
      layer.xtnAiim = {
        isPlanLayer: true,
        requiresFacilityMode: true,
        source: task.plan.peopleSource
      };
      promises.push(task.plan.peopleSource.checkSchema());
    }
    if (unitsLayer) {
      let layer = unitsLayer;
      task.plan.unitsSource = new Source({
        key: sourceUtil.getUnitsSourceKey(),
        name: layer.title,
        url: layer.url+"/"+layer.layerId,
        layer2D: layer
      });
      layer.xtnAiim = {
        isPlanLayer: true,
        requiresFacilityMode: true,
        source: task.plan.unitsSource
      };
      promises.push(task.plan.unitsSource.checkSchema());
    }
    return Promise.all(promises);
  }

}
